import React from "react";
import { Divider, List, ListItem, ListItemText, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { ListItemLink } from "./list-item-link.component";

type P = {
  menuId?: string | null;
  isContentBelow?: boolean;
};

export const HoverMenuContent: React.FC<P> = ({ menuId, isContentBelow = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    userRoles: { isCapacityResponsible, isFleetAdmin, isDeviceResponsible }
  } = useSelector((state: RootState) => state.login);

  const nestedListItemStyle = isContentBelow
    ? {
        paddingLeft: "25%",
        height: "40px"
      }
    : undefined;
  const commonListItemStyle = {
    fontSize: "0.8rem",
    boxShadow: "inset 3px 0 0 0 " + theme.palette.primary.main
  };

  switch (menuId) {
    case "home":
      return <SimpleMenuItem to={"/"} primary={t("Home")} testid="hover-content-home" />;

    case "booking":
      return (
        <SimpleMenuItem to={"/booking/0"} primary={t("Booking")} testid="hover-content-booking" />
      );

    case "schedule": // not used currently
      return (
        <SimpleMenuItem to={"/schedule"} primary={t("Schedule")} testid="hover-content-schedule" />
      );

    case "connections":
      return (
        <SimpleMenuItem
          to={"/connections"}
          primary={t("Connections")}
          testid="hover-content-connections"
        />
      );

    case "supervisor":
      return (
        <SimpleMenuItem
          to={"/supervisor"}
          primary={t("Supervisor")}
          testid="hover-content-supervisor"
        />
      );

    case "administration":
      if (isFleetAdmin && !isCapacityResponsible && !isDeviceResponsible) {
        return (
          <List disablePadding>
            <ListItemLink
              to={"/admin/fleet"}
              primary={t("Drawer_Fleet")}
              navLinkStyleProps={{ ...nestedListItemStyle, ...commonListItemStyle }}
              data-testid="hover-content-fleetAdmin"
            />
            <ListItemLink
              to={"/capacity/fleet"}
              primary={t("Drawer_Fleet")}
              navLinkStyleProps={{ ...nestedListItemStyle, ...commonListItemStyle }}
              data-testid="hover-content-capacity-fleet"
            />
            <ListItemLink
              to={"/equipment/inventory"}
              primary={t("Drawer_Inventory")}
              navLinkStyleProps={{ ...nestedListItemStyle, ...commonListItemStyle }}
              data-testid="hover-content-inventory"
            />
          </List>
        );
      }
      return (
        <SimpleMenuItem
          to={"/administration"}
          primary={t("Drawer_Administration")}
          testid="hover-content-administration"
        />
      );

    case "organization":
      return (
        <SimpleMenuItem
          to={"/admin/organization"}
          primary={t("Organization")}
          testid="hover-content-organization"
        />
      );

    case "facility": // buildings - create/edit the floor plan
      return (
        <SimpleMenuItem
          to={"/facility/schedule"}
          primary={t("Drawer_Location")}
          testid="hover-content-facility"
        />
      );

    case "bookingApproval_request":
      return (
        <SimpleMenuItem
          to={"/booking/requests"}
          primary={t("Drawer_Request")}
          testid="hover-content-bookingApproval_request"
        />
      );

    case "inventory":
      return (
        <SimpleMenuItem
          to={"/equipment/inventory"}
          primary={t("Drawer_Inventory")}
          testid="hover-content-inventory"
        />
      );

    case "service":
      return (
        <List>
          {!isContentBelow && (
            <>
              <ListItem>
                <ListItemText sx={{ fontSize: "0.8rem" }}>{t("Service")}</ListItemText>
              </ListItem>
              <Divider
                sx={{
                  opacity: "0.6"
                }}
              />
            </>
          )}
          <SimpleMenuItem
            to={"/admin/service/management"}
            primary={t("Drawer_Sub_ServiceManagement")}
            testid="hover-service-management"
          />
          <SimpleMenuItem
            to={"/admin/service/bookings"}
            primary={t("Drawer_Sub_Bookings")}
            testid="hover-service-bookings"
          />
          <SimpleMenuItem
            to={"/admin/service/orders"}
            primary={t("Drawer_Sub_Orders")}
            testid="hover-service-orders"
          />
        </List>
      );
    case "workplace-category-report":
      return (
        <SimpleMenuItem
          to={"/equipment/capacity"}
          primary={t("Drawer_Category Report")}
          testid="hover-content-category-report"
        />
      );
    case "management":
      return (
        <SimpleMenuItem
          to={"/management"}
          primary={t("Management")}
          testid="hover-content-management"
        />
      );
    case "capacity":
      return (
        <List>
          {!isContentBelow && (
            <>
              <ListItem>
                <ListItemText sx={{ fontSize: "0.8rem" }}>{t("Capacity")}</ListItemText>
              </ListItem>
              <Divider
                sx={{
                  opacity: "0.6"
                }}
              />
            </>
          )}
          {isCapacityResponsible && (
            <SimpleMenuItem
              to={"/capacity/map"}
              primary={t("Heatmap")}
              testid="hover-content-capacity-map"
            />
          )}
          {isCapacityResponsible && (
            <SimpleMenuItem
              to={"/capacity/assistant"}
              primary={t("Assistant")}
              testid="hover-content-capacity-assistant"
            />
          )}
          {isCapacityResponsible && (
            <SimpleMenuItem
              to={"/capacity/departmentReport"}
              primary={t("DistributionReport")}
              testid="hover-content-capacity-department-kpi"
            />
          )}
          {isFleetAdmin && (
            <SimpleMenuItem
              to={"/capacity/fleet"}
              primary={t("Drawer_Fleet")}
              testid="hover-content-capacity-fleet"
            />
          )}
        </List>
      );
    case "hr-report":
      return (
        <SimpleMenuItem to={"/hr-report"} primary={t("HR")} testid="hover-content-hr-report" />
      );
    case "administrators":
      return (
        <SimpleMenuItem
          to={"/admin/admins"}
          primary={t("Administrators")}
          testid="hover-content-administrators"
        />
      );
    /** not used currently below */
    case "booking-activities":
      return (
        <List>
          {!isContentBelow && (
            <>
              <ListItem>
                <ListItemText sx={{ fontSize: "0.8rem" }}>{t("Booking Activities")}</ListItemText>
              </ListItem>
              <Divider
                sx={{
                  opacity: "0.6"
                }}
              />
            </>
          )}
          <ListItemLink
            navLinkStyleProps={{ ...nestedListItemStyle, ...commonListItemStyle }}
            to={"/booking-activities/occupation"}
            primary={t("Occupation")}
          />
          <ListItemLink
            navLinkStyleProps={{ ...nestedListItemStyle, ...commonListItemStyle }}
            to={"/booking-activities/contact-tracing"}
            primary={t("Contact Tracing")}
          />
        </List>
      );
    case "finance":
      return (
        <SimpleMenuItem to={"/finance"} primary={t("Finance")} testid="hover-content-finance" />
      );
    case "location":
      return (
        <SimpleMenuItem to={"/location"} primary={t("Location")} testid="hover-content-location" />
      );
    case "notifications":
      return (
        <SimpleMenuItem
          to={"/notifications"}
          primary={t("Notifications")}
          testid="hover-content-notifications"
        />
      );
    case "fleet":
      return (
        <SimpleMenuItem
          to={"/admin/fleet"}
          primary={t("Drawer_Fleet")}
          testid="hover-content-fleetAdmin"
        />
      );
    case "visitors":
      return (
        <SimpleMenuItem
          to={"/admin/visitors"}
          primary={t("Drawer_Visitors")}
          testid="hover-content-visitorsAdmin"
        />
      );
    default:
      return null;
  }
};
type SimpleMenuItemProps = {
  to: string;
  primary: string;
  testid: string;
  isContentBelow?: boolean;
};
export const SimpleMenuItem: React.FC<SimpleMenuItemProps> = ({
  to,
  primary,
  testid,
  isContentBelow = false
}) => {
  const theme = useTheme();
  const nestedListItemStyle = isContentBelow
    ? {
        paddingLeft: "25%",
        height: "40px"
      }
    : undefined;
  const commonListItemStyle = {
    fontSize: "0.8rem",
    boxShadow: "inset 3px 0 0 0 " + theme.palette.primary.main
  };
  return (
    <List disablePadding>
      <ListItemLink
        to={to}
        primary={primary}
        navLinkStyleProps={{ ...nestedListItemStyle, ...commonListItemStyle }}
        data-testid={`${testid}`}
      />
    </List>
  );
};
